import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';

/**
 * Lightweight service that signals when the authenticated user's access_token
 * has been fully stored in localStorage and API calls can safely be made.
 * Kept separate to avoid circular DI between OktaAuthService and UpdatePasswordService.
 *
 * On page refresh, access_token is already in localStorage so the subject is
 * initialised as `true` immediately — no need to wait for notifyTokenReady().
 * On first login, it starts as `false` and notifyTokenReady() flips it once
 * createLocalStorageUser() completes the client-credentials token fetch.
 */
@Injectable({ providedIn: 'root' })
export class AuthStateService {

  private static isTokenInStorage(): boolean {
    try {
      const user = JSON.parse(localStorage.getItem('currentUser'));
      return !!(user?.access_token);
    } catch {
      return false;
    }
  }

  private _tokenReady$ = new BehaviorSubject<boolean>(AuthStateService.isTokenInStorage());

  /** Emits once (and completes) when access_token is available. */
  readonly tokenReady$: Observable<boolean> = this._tokenReady$.asObservable().pipe(
    filter(ready => ready),
    take(1)
  );

  notifyTokenReady(): void {
    this._tokenReady$.next(true);
  }
}